import { FlowAction, FlowMetadata } from '../core/types/chat.types'

type FlowMessageLike = {
  id: string
  messageId?: string
  role?: 'user' | 'assistant'
  flowMetadata?: FlowMetadata
}

export const cleanMessageId = (messageId?: string | null): string | null => {
  if (!messageId) return null
  const cleaned = messageId.replace(/^MESSAGE#/, '').trim()
  return cleaned || null
}

export const sameMessageId = (left?: string | null, right?: string | null): boolean => {
  if (!left || !right) return false
  return left === right || cleanMessageId(left) === cleanMessageId(right)
}

export const normalizeFlowMetadata = (
  meta?: FlowMetadata | null
): FlowMetadata | null | undefined => {
  if (!meta) {
    return meta
  }

  if (meta.actionsAvailable && !meta.availableActions) {
    return {
      ...meta,
      availableActions: meta.actionsAvailable,
    }
  }

  return meta
}

export const getAvailableFlowActions = (meta?: FlowMetadata | null): FlowAction[] => {
  const normalized = normalizeFlowMetadata(meta)
  return normalized?.availableActions || normalized?.actionsAvailable || []
}

export const findLatestFlowMetadata = (
  messages: Array<{ flowMetadata?: FlowMetadata | null }>
): FlowMetadata | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const metadata = normalizeFlowMetadata(messages[index]?.flowMetadata)
    if (metadata) {
      return metadata
    }
  }

  return null
}

export const budgetStepToCamelCase = (step: string): string => {
  const parts = step.toLowerCase().split('_')
  return (
    parts[0] +
    parts
      .slice(1)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('')
  )
}

export const isBudgetCompleteMetadata = (meta?: FlowMetadata | null): boolean => {
  const normalized = normalizeFlowMetadata(meta)
  const flow = normalized?.flow
  const step = normalized?.step

  return (
    typeof flow === 'string' &&
    flow.toUpperCase() === 'BUDGET' &&
    typeof step === 'string' &&
    step.toUpperCase() === 'COMPLETE'
  )
}

const isCompareAssistantMessage = (message: FlowMessageLike): boolean => {
  if (message.role === 'user') {
    return false
  }

  const flow = normalizeFlowMetadata(message.flowMetadata)?.flow
  return typeof flow === 'string' && flow.toUpperCase() === 'COMPARE'
}

const findLatestCompareInitialAnchorIndex = (messages: FlowMessageLike[]): number => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (!message || !isCompareAssistantMessage(message)) continue

    const metadata = normalizeFlowMetadata(message.flowMetadata)
    const phase = metadata?.phase
    const step = metadata?.step
    if (typeof phase !== 'string' || phase.toUpperCase() !== 'INITIAL') continue
    if (typeof step === 'string' && step.toUpperCase() === 'DOC_TYPE_SELECTION') {
      return index
    }
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (!message || !isCompareAssistantMessage(message)) continue

    const phase = normalizeFlowMetadata(message.flowMetadata)?.phase
    if (typeof phase === 'string' && phase.toUpperCase() === 'INITIAL') {
      return index
    }
  }

  return -1
}

export const findCompareReferenceMessageIdFromMessages = (
  messages: FlowMessageLike[]
): string | null => {
  const anchorIndex = findLatestCompareInitialAnchorIndex(messages)
  const startIndex = anchorIndex >= 0 ? anchorIndex + 1 : 0

  for (let index = startIndex; index < messages.length; index += 1) {
    const message = messages[index]
    if (!message || !isCompareAssistantMessage(message)) continue

    const phase = normalizeFlowMetadata(message.flowMetadata)?.phase
    if (typeof phase !== 'string' || phase.toUpperCase() !== 'FOLLOWUP') continue

    const messageId = cleanMessageId(message.messageId || message.id)
    if (messageId) {
      return messageId
    }
  }

  return null
}
